// Who the console serves and who is looking: GET /me, once per session. The organisation (the tenant) is data
// (Majlis is the product; Foodics its first organisation). The role the server returns is the one that counts;
// the token's claims are only a first guess. The person's workspaces (their teams, 2026-10-16) come with it, and
// the one they work in is remembered per organisation and person, on this device only.
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { fetchMe } from "@/api/console";
import type { Me } from "@/api/types";
import { useApi } from "@/composables/useApi";

export const useWorkspaceStore = defineStore("workspace", () => {
  const me = ref<Me | null>(null);
  const state = ref<"idle" | "loading" | "ready" | "error">("idle");
  const error = ref("");
  const chosen = ref("");

  const tenantName = computed(() => me.value?.tenant?.name ?? "");
  const tenantInitial = computed(() => (tenantName.value || "?").slice(0, 1).toUpperCase());
  const workspaces = computed(() => me.value?.teams ?? []);
  /** The workspace the person works in: the one they chose, if still theirs, else their first. */
  const current = computed(() => workspaces.value.find((w) => w.key === chosen.value) ?? workspaces.value[0] ?? null);
  const storeKey = () => `majlis.workspace:${me.value?.tenant?.slug ?? ""}:${me.value?.sub ?? ""}`;

  async function load(force = false): Promise<void> {
    if (state.value === "loading" || (state.value === "ready" && !force)) return;
    state.value = "loading";
    try {
      me.value = await fetchMe(useApi().client);
      try { chosen.value = window.localStorage.getItem(storeKey()) ?? ""; } catch { chosen.value = ""; }
      state.value = "ready";
      error.value = "";
    } catch (e) {
      state.value = "error";
      error.value = (e as { message?: string }).message ?? "couldn't load your organisation and workspaces";
    }
  }
  function choose(key: string): void {
    chosen.value = key;
    try { window.localStorage.setItem(storeKey(), key); } catch { /* a convenience only */ }
  }

  return { me, state, error, tenantName, tenantInitial, workspaces, current, load, choose };
});
